// src/modules/app-views/account/pages/Init/modals/ChangeEmailModal/Step3.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Alert, Typography } from 'antd';
import { MailOutlined, NumberOutlined } from '@ant-design/icons';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import appService from 'services/appService';
import authService from 'services/authService';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const VerifyNewEmailStepContent = ({
  storedUser,
  newEmailDataRef,
  codeSent,
  setCodeSent,
}) => {
  const [isSending, setIsSending] = useState(false);

  const newEmail = newEmailDataRef.current.email;

  // Send code automatically when step opens
  useEffect(() => {
    if (!codeSent && newEmail && !isSending) {
      sendVerificationCode();
    }
  }, [codeSent, newEmail]);

  const sendVerificationCode = async () => {
    if (isSending) return;

    setIsSending(true);

    try {
      const success = await authService.sendEmailRegistrationCode(
        {
          name: storedUser?.name,
          email: newEmail,
          emailData: { highTitle: 'verify_new_email' },
        },
        'email_must_not_exists'
      );

      if (success && success !== 'error') {
        setCodeSent(true);

        appService.notification({
          key: 'newEmailCodeSentKey',
          flag: 'info',
          title: newEmail,
          description: 'verification_code_sent_email',
          duration: 0,
          icon: <MailOutlined />,
        });
      } else if (success === false) {
        // Email already exists
        await appService.message({ flag: 'w', text: 'email_already_in_use' });
      } else {
        await appService.message({ flag: 'e', text: 'failed_to_send_verification_code' });
      }
    } catch (err) {
      console.error('[ChangeEmailModal] Error sending code:', err);
      await appService.message({ flag: 'e', text: 'failed_to_send_verification_code' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        icon={<MailOutlined />}
        title={translateX('verify_new_email')}
        description={translateX('verify_new_email_description', {
          '%EMAIL%': newEmail,
        })}
        style={{ marginBottom: 16 }}
      />

      <SimpleInput
        name="newEmailCode"
        label="verification_code"
        minLength={4}
        maxLength={4}
        required
        prefix={<NumberOutlined className="text-primary" />}
        placeholder="0000"
      />

      {codeSent && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {translateX('didnt_receive_code')}{' '}
          <a onClick={sendVerificationCode} style={{ cursor: isSending ? 'wait' : 'pointer' }}>
            {translateX('resend_code')}
          </a>
        </Text>
      )}
    </>
  );
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
const validateNewEmailCode = async ({
  form,
  setLoading,
  goFocus,
  newEmailDataRef,
}) => {
  const code = form?.getFieldValue('newEmailCode');
  const newEmail = newEmailDataRef.current.email;

  if (!code || code.trim() === '') {
    await appService.message({ flag: 'w', text: 'verification_code_required' });
    goFocus?.('newEmailCode', true);
    return false;
  }

  try {
    setLoading(true);

    const success = await authService.validateEmailCode({
      email: newEmail,
      emailCode: code,
    });

    if (success) {
      appService.notificationDestroy('newEmailCodeSentKey');
      return true;
    } else {
      await appService.message({ flag: 'w', text: 'invalid_verification_code' });
      goFocus?.('newEmailCode', true);
      return false;
    }
  } catch (err) {
    console.error('[ChangeEmailModal] Error validating code:', err);
    await appService.message({ flag: 'e', text: 'verification_failed' });
    goFocus?.('newEmailCode', true);
    return false;
  } finally {
    setLoading(false);
  }
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
const verifyNewEmailStep = ({ storedUser, newEmailDataRef }) => {
  let codeSent = false;

  return {
    stepTitle: 'verification',
    stepTooltip: 'verify_new_email',
    content: ({ form, goFocus }) => {
      const [localCodeSent, setLocalCodeSent] = useState(codeSent);

      // Sync with closure
      useEffect(() => {
        codeSent = localCodeSent;
      }, [localCodeSent]);

      return (
        <VerifyNewEmailStepContent
          storedUser={storedUser}
          newEmailDataRef={newEmailDataRef}
          codeSent={localCodeSent}
          setCodeSent={setLocalCodeSent}
        />
      );
    },
    validationFields: ['newEmailCode'],
    nextButtonText: 'change_email',
    nextButtonIcon: <MailOutlined />,
    isConfirmStep: true,
    onNext: async ({ form, setLoading, goFocus }) =>
      validateNewEmailCode({ form, setLoading, goFocus, newEmailDataRef }),
    onEnter: ({ goFocus }) => {
      setTimeout(() => goFocus?.('newEmailCode'), 100);
    },
  };
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default verifyNewEmailStep;
